import { useEffect, useState } from 'react';
import {
  api,
  daysUntil,
  formatBonuses,
  formatDate,
  pluralDays,
  RequestFailed,
  type Accrual,
  type ContentBlock,
  type Product,
} from './api';
import { useAsync, useIdempotencyKey } from './hooks';
import { Link } from './router';

// ─────────────────────────── Общие мелочи ───────────────────────────

const ACCRUAL_STATE_LABEL: Record<string, string> = {
  PENDING: 'На подтверждении',
  HELD: 'В холде',
  AVAILABLE: 'Начислено',
  CANCELLED: 'Отменено',
  CLAWED_BACK: 'Списано после возврата',
  REJECTED: 'Без начисления',
  QUARANTINED: 'Разбираемся',
};

const STATE_CLASS: Record<string, string> = {
  PENDING: 'pending',
  HELD: 'hold',
  AVAILABLE: 'available',
  CANCELLED: 'cancelled',
  CLAWED_BACK: 'cancelled',
  REJECTED: 'rejected',
  QUARANTINED: 'pending',
};

const REJECT_LABEL: Record<string, string> = {
  ORDER_TYPE_DISABLED: 'За такой заказ бонусы не начисляются',
  NO_REFERRER: 'Заказ пришёл не по вашей ссылке',
  SELF_REFERRAL: 'Покупка по собственной ссылке',
  BELOW_MIN_AMOUNT: 'Сумма заказа ниже порога',
  UNKNOWN_ORDER_TYPE: 'Разбираемся с заказом',
  NO_RULE: 'Разбираемся с заказом',
};

function Loading({ rows = 3 }: { rows?: number }) {
  return (
    <div className="grid" style={{ marginTop: 16 }}>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skeleton" style={{ height: 84 }} />
      ))}
    </div>
  );
}

function ErrorNotice({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="notice error" style={{ marginTop: 16 }}>
      {message}
      {onRetry && (
        <>
          {' '}
          <button className="link" onClick={onRetry}>
            Повторить
          </button>
        </>
      )}
    </div>
  );
}

// ─────────────────────────── Вход (ТЗ §2.1, §2.3) ───────────────────────────

export function LoginPage() {
  const params = new URLSearchParams(location.search);
  const error = params.get('error');
  const redirect = params.get('redirect') ?? '/';

  return (
    <div className="center">
      <div style={{ maxWidth: 420 }}>
        <h1 style={{ marginTop: 0 }}>Бонусы за друзей</h1>
        <p className="muted">
          Приглашайте друзей по своей ссылке и получайте бонусы за каждого, кто начнёт учиться.
        </p>

        {error === 'vk_declined' && (
          <div className="notice error" style={{ margin: '20px 0', textAlign: 'left' }}>
            Вход через ВКонтакте не завершён. Попробуйте ещё раз.
          </div>
        )}

        <a
          className="button"
          style={{ display: 'inline-block', textDecoration: 'none', marginTop: 12 }}
          href={`/auth/vk/start?redirect=${encodeURIComponent(redirect)}`}
        >
          Войти через ВКонтакте
        </a>
      </div>
    </div>
  );
}

// ─────────────────────────── Главная ───────────────────────────

export function DashboardPage() {
  const { data, loading, error, reload } = useAsync(() => api.me(), []);

  // Аккаунт переехал на канонический id после того, как нашлась связь
  // с основной платформой: молча перевыпускаем сессию, ученику об этом
  // знать незачем.
  useEffect(() => {
    if (data?.relogin) location.href = '/auth/vk/start?redirect=/';
  }, [data?.relogin]);

  if (loading) return <Loading rows={2} />;
  if (error || !data) return <ErrorNotice message={error ?? 'Нет данных'} onRetry={reload} />;

  const { user, balance, summary, linked } = data;

  return (
    <>
      <h1>Привет{user.displayName ? `, ${user.displayName.split(' ')[0]}` : ''}</h1>

      {!linked && <NotLinkedNotice />}

      {balance.inDebt && (
        <div className="notice warn" style={{ marginBottom: 16 }}>
          По одному из заказов оформлен возврат, и бонусы за него списаны. Баланс уйдёт в плюс, как только
          начислятся следующие приглашения.
        </div>
      )}

      <div className="grid cols-3">
        <div className="card">
          <div className="stat-value">{formatBonuses(balance.available)}</div>
          <div className="stat-label">Доступно к трате</div>
          <div className="stat-hint">
            <Link to="/shop">Потратить в лавке →</Link>
          </div>
        </div>
        <div className="card">
          <div className="stat-value">{formatBonuses(balance.hold)}</div>
          <div className="stat-label">В холде</div>
          <div className="stat-hint">Станут доступны через {pluralDays(summary.holdDays)} после оплаты</div>
        </div>
        <div className="card">
          <div className="stat-value">{formatBonuses(balance.pending)}</div>
          <div className="stat-label">На подтверждении</div>
          <div className="stat-hint">Друг оформил заказ, но ещё не оплатил</div>
        </div>
      </div>

      <h2>Ваша ссылка</h2>
      <div className="card">
        {user.referralUrl ? (
          <>
            <p className="muted small" style={{ marginTop: 0 }}>
              Отправьте её другу. Когда он оплатит обучение, вам начислятся бонусы.
            </p>
            <CopyField value={user.referralUrl} />
          </>
        ) : (
          <p className="muted" style={{ margin: 0 }}>
            {linked
              ? 'Реферальная ссылка ещё не создана на основной платформе. Загляните позже.'
              : 'Ссылка появится здесь, как только профиль свяжется с основной платформой.'}
          </p>
        )}
      </div>

      <h2>Сколько дают за приглашённого</h2>
      <div className="card">
        <div className="row between">
          <div>
            <div className="stat-value" style={{ fontSize: 24 }}>
              {formatBonuses(summary.next.bonuses)}
            </div>
            <div className="stat-label">За следующего, {summary.next.ordinal}-го приглашённого</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="stat-value" style={{ fontSize: 24 }}>
              {summary.invitedCount}
            </div>
            <div className="stat-label">Уже приглашено</div>
          </div>
        </div>

        <div className="scale">
          {summary.scale.map((tier) => {
            const isCurrent =
              summary.next.ordinal >= tier.fromOrdinal &&
              (tier.toOrdinal === null || summary.next.ordinal <= tier.toOrdinal);
            return (
              <div key={tier.fromOrdinal} className={`scale-row${isCurrent ? ' current' : ''}`}>
                <span>
                  {tier.toOrdinal === null
                    ? `${tier.fromOrdinal}-й и далее`
                    : tier.fromOrdinal === tier.toOrdinal
                      ? `${tier.fromOrdinal}-й`
                      : `${tier.fromOrdinal}–${tier.toOrdinal}-й`}
                </span>
                <span>{formatBonuses(tier.bonuses)} бонусов</span>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

/**
 * Ученик вошёл через ВКонтакте, но его ещё не нашли в базе основной платформы
 * (ТЗ §2.2–2.3). Это рабочее состояние: кабинет открыт, а текст подсказки
 * редактируется в админке — здесь он лишь запасной.
 */
function NotLinkedNotice() {
  const { data } = useAsync(() => api.content(), []);
  const text =
    data?.items.find((b) => b.key === 'error.VK_USER_NOT_LINKED')?.body ??
    'Мы пока не нашли ваш аккаунт на основной платформе. Привяжите ВКонтакте в профиле обучения — после этого здесь появятся ваша ссылка и бонусы.';

  return (
    <div className="notice warn" style={{ marginBottom: 16 }}>
      {text}
    </div>
  );
}

function CopyField({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="copy-field">
      <input readOnly value={value} onFocus={(e) => e.currentTarget.select()} />
      <button
        className="secondary"
        style={{ padding: '6px 12px', fontSize: 14 }}
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(value);
          } catch {
            // Буфер обмена недоступен (нет https или отказано в доступе) —
            // поле всё равно выделяется, копирование вручную работает.
          }
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        }}
      >
        {copied ? 'Скопировано' : 'Копировать'}
      </button>
    </div>
  );
}

// ─────────────────────────── Приглашённые ───────────────────────────

export function AccrualsPage() {
  const [cursor, setCursor] = useState<string | undefined>();
  const [all, setAll] = useState<Accrual[]>([]);
  const { data, loading, error, reload } = useAsync(async () => {
    const page = await api.accruals(cursor);
    setAll((prev) => (cursor ? [...prev, ...page.items] : page.items));
    return page;
  }, [cursor]);

  if (loading && all.length === 0) return <Loading />;
  if (error && all.length === 0) return <ErrorNotice message={error} onRetry={reload} />;

  return (
    <>
      <h1>Приглашённые</h1>
      {all.length === 0 ? (
        <div className="card">
          <p className="muted" style={{ margin: 0 }}>
            Пока никто не купил по вашей ссылке. Как только это произойдёт, начисление появится здесь.
          </p>
        </div>
      ) : (
        <div className="card">
          <div className="list">
            {all.map((a) => {
              const left = a.state === 'HELD' ? daysUntil(a.releaseAt) : null;
              return (
                <div key={a.id} className="list-item">
                  <div className="grow">
                    <div className="title">
                      {a.ordinal ? `${a.ordinal}-й приглашённый` : 'Приглашённый'}
                      {a.state === 'REJECTED' && a.rejectReason && (
                        <span className="muted small"> — {REJECT_LABEL[a.rejectReason] ?? 'Без начисления'}</span>
                      )}
                    </div>
                    <div className="muted small">
                      {a.paidAt ? `Оплачено ${formatDate(a.paidAt)}` : `Заказ от ${formatDate(a.createdAt)}`}
                      {left !== null && left > 0 && ` · осталось ${pluralDays(left)}`}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div className="title">{a.amount > 0 ? `+${formatBonuses(a.amount)}` : '—'}</div>
                    <span className={`badge ${STATE_CLASS[a.state] ?? ''}`}>
                      {ACCRUAL_STATE_LABEL[a.state] ?? a.state}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
          {data?.nextCursor && (
            <button
              className="secondary"
              style={{ marginTop: 16, width: '100%' }}
              disabled={loading}
              onClick={() => setCursor(data.nextCursor!)}
            >
              {loading ? 'Загружаем…' : 'Показать ещё'}
            </button>
          )}
        </div>
      )}
    </>
  );
}

// ─────────────────────────── Лавка (ТЗ §5) ───────────────────────────

export function ShopPage() {
  const catalog = useAsync(() => api.catalog(), []);
  const me = useAsync(() => api.me(), []);
  const [purchased, setPurchased] = useState<{ title: string; code: string | null; expiresAt: string | null } | null>(null);

  if (catalog.loading || me.loading) return <Loading />;
  if (catalog.error || !catalog.data) return <ErrorNotice message={catalog.error ?? 'Нет данных'} onRetry={catalog.reload} />;

  const available = me.data?.balance.available ?? 0;
  const linked = me.data?.linked ?? true;
  const { categories, products } = catalog.data;

  return (
    <>
      <div className="row between" style={{ marginTop: 28 }}>
        <h1 style={{ margin: 0 }}>Лавка</h1>
        <span className="badge available">{formatBonuses(available)} бонусов</span>
      </div>

      {!linked && <NotLinkedNotice />}

      {purchased && (
        <div className="card" style={{ marginTop: 16 }}>
          <div className="title">Готово — «{purchased.title}»</div>
          {purchased.code ? (
            <>
              <p className="muted small">Ваш код. Он сохранится в истории покупок.</p>
              <div className="code-box">{purchased.code}</div>
              {purchased.expiresAt && (
                <p className="muted small" style={{ marginBottom: 0 }}>
                  Действует до {formatDate(purchased.expiresAt)}
                </p>
              )}
            </>
          ) : (
            <p className="muted small" style={{ marginBottom: 0 }}>Покупка оформлена.</p>
          )}
        </div>
      )}

      {products.length === 0 && (
        <div className="card" style={{ marginTop: 16 }}>
          <p className="muted" style={{ margin: 0 }}>Скоро здесь появятся товары.</p>
        </div>
      )}

      {categories
        .filter((c) => products.some((p) => p.category_slug === c.slug))
        .map((category) => (
          <section key={category.id}>
            <h2>{category.title}</h2>
            <div className="grid cols-2">
              {products
                .filter((p) => p.category_slug === category.slug)
                .map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    balance={available}
                    linked={linked}
                    onPurchased={(r) => {
                      setPurchased({ title: r.title, code: r.code, expiresAt: r.codeExpiresAt });
                      me.reload();
                      catalog.reload();
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                  />
                ))}
            </div>
          </section>
        ))}
    </>
  );
}

function ProductCard({
  product,
  balance,
  linked,
  onPurchased,
}: {
  product: Product;
  balance: number;
  linked: boolean;
  onPurchased: (r: { title: string; code: string | null; codeExpiresAt: string | null }) => void;
}) {
  const idempotencyKey = useIdempotencyKey(product.id);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const notEnough = balance < product.price_bonuses;
  const blocked = !linked || !product.available || notEnough || busy;

  return (
    <div className="card product">
      <div className="grow">
        <div className="title">{product.title}</div>
        {product.description && (
          <p className="muted small" style={{ marginBottom: 0 }}>
            {product.description}
          </p>
        )}
      </div>

      <div className="row between">
        <span className="price">{formatBonuses(product.price_bonuses)}</span>
        {product.stock !== null && product.stock <= 5 && product.stock > 0 && (
          <span className="badge hold">осталось {product.stock}</span>
        )}
        {!product.available && <span className="badge">нет в наличии</span>}
      </div>

      {error && <div className="notice error small">{error}</div>}

      <button
        disabled={blocked}
        onClick={async () => {
          setBusy(true);
          setError(null);
          try {
            const r = await api.buy(product.id, idempotencyKey);
            onPurchased(r);
          } catch (e) {
            setError(e instanceof RequestFailed ? e.error.message : 'Не удалось оформить покупку');
          } finally {
            setBusy(false);
          }
        }}
      >
        {busy
          ? 'Оформляем…'
          : !linked
            ? 'Нужна связь с обучением'
            : notEnough
              ? `Не хватает ${formatBonuses(product.price_bonuses - balance)}`
              : 'Получить'}
      </button>
    </div>
  );
}

// ─────────────────────────── История покупок (ТЗ §5.6) ───────────────────────────

export function PurchasesPage() {
  const { data, loading, error, reload } = useAsync(() => api.purchases(), []);

  if (loading) return <Loading />;
  if (error || !data) return <ErrorNotice message={error ?? 'Нет данных'} onRetry={reload} />;

  return (
    <>
      <h1>Мои покупки</h1>
      {data.items.length === 0 ? (
        <div className="card">
          <p className="muted" style={{ margin: 0 }}>
            Пока пусто. <Link to="/shop">Загляните в лавку</Link>.
          </p>
        </div>
      ) : (
        <div className="card">
          <div className="list">
            {data.items.map((p) => (
              <div key={p.id} className="list-item" style={{ alignItems: 'flex-start' }}>
                <div className="grow">
                  <div className="title">{p.title}</div>
                  <div className="muted small">{formatDate(p.createdAt)}</div>
                  {p.code && <div className="code-box" style={{ marginTop: 8 }}>{p.code}</div>}
                  {p.codeExpiresAt && (
                    <div className="muted small" style={{ marginTop: 6 }}>
                      Действует до {formatDate(p.codeExpiresAt)}
                    </div>
                  )}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div className="title">−{formatBonuses(p.priceBonuses)}</div>
                  {p.state === 'REFUNDED' && <span className="badge cancelled">возвращено</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

// ─────────────────────────── Правила и вопросы (ТЗ §7.4) ───────────────────────────

export function RulesPage() {
  const { data, loading, error, reload } = useAsync(() => api.content(), []);
  if (loading) return <Loading />;
  if (error || !data) return <ErrorNotice message={error ?? 'Нет данных'} onRetry={reload} />;

  const blocks = data.items.filter((b) => b.key.startsWith('page.') || b.kind === 'FAQ');

  return (
    <>
      <h1>Как это работает</h1>
      {blocks.map((block) => (
        <section key={block.key}>
          <h2>{block.title}</h2>
          <div className="card">{block.kind === 'FAQ' ? <Faq block={block} /> : <Prose text={block.body} />}</div>
        </section>
      ))}
    </>
  );
}

function Faq({ block }: { block: ContentBlock }) {
  if (block.items.length === 0) return <p className="muted" style={{ margin: 0 }}>Вопросов пока нет.</p>;
  return (
    <>
      {block.items.map((item, i) => (
        <details key={i} className="faq">
          <summary>{item.question}</summary>
          <p>{item.answer}</p>
        </details>
      ))}
    </>
  );
}

/**
 * Текст из админки выводится как обычный текст с разбивкой по абзацам.
 * Никакого dangerouslySetInnerHTML: содержимое редактируется людьми, и
 * подставлять его в разметку как есть — прямой путь к XSS.
 */
function Prose({ text }: { text: string }) {
  return (
    <>
      {text.split(/\n{2,}/).map((paragraph, i) => (
        <p key={i} style={{ margin: i === 0 ? '0 0 12px' : '0 0 12px' }}>
          {paragraph}
        </p>
      ))}
    </>
  );
}
